import { useEffect, useState } from "react"
import { useController, Controller } from "react-hook-form"
import CurrencyInput from "react-currency-input-field"
import classNames from "classnames"

// Components
import { TextMessage } from "@mobi/ds"
import If from "@mobi/components/If"

// Utils
import numberFormatter from "@mobi/utils/formatters/number"
import mergeDeep from "@mobi/utils/object/mergeDeep"

// Styles
import "./Currency.scss"

// Assets
import EditIcon from "../../../../static/icons/outlined/editar.svg"

const Currency = ({
  name,
  id = "",
  type = "text",
  inputmode = "numeric",
  pattern = "[R$0-9.,]*",
  label = "",
  control,
  defaultValue = 0,
  description = "",
  required = true,
  rules = { min: null, max: null },
  validators: { rules: propRules = {}, messages: propMessages = {} } = {},
  disabled = false,
  decimalsLimit = 2,
  decimalScale = 2,
  decimalSeparator = ",",
  groupSeparator = ".",
  onBlur,
  onChange,
  shouldResetOn,
  ...props
}) => {
  const { "aria-required": ariaRequired } = props
  const DEFAULT_MESSAGES = {
    required: "campo obrigatório",
    ...(rules.min && {
      min: `o valor deve ser maior que R$ ${numberFormatter({
        value: rules.min,
      })}`,
    }),
    ...(rules.max && {
      max: `o valor deve ser menor que R$ ${numberFormatter({
        value: rules.max,
      })}`,
    }),
  }

  const DEFAULT_RULES = {
    validate: {
      ...(rules.min && { min: (value) => value >= rules.min }),
      ...(rules.max && { max: (value) => value <= rules.max }),
    },
  }

  const {
    field,
    fieldState: { invalid, error, isTouched, isDirty },
  } = useController({ name, control })

  const isInvalid = isTouched && invalid && isDirty

  const getDefaultValue = () => {
    if (defaultValue === 0) return null
    return typeof defaultValue === "number" ? defaultValue : null
  }

  const [isToRender, setIsToRender] = useState(false)
  const [hasValue, setHasValue] = useState(!!getDefaultValue())

  useEffect(() => {
    typeof defaultValue === "number" && setIsToRender(true)
  }, [defaultValue])

  useEffect(() => {
    if (typeof defaultValue === "number") {
      setIsToRender(false)
      setTimeout(() => {
        setIsToRender(true)
      }, 10)
    }
  }, [shouldResetOn])

  useEffect(() => {
    setHasValue(!!getDefaultValue())
  }, [defaultValue])

  const formRules = mergeDeep(DEFAULT_RULES, propRules, { required })
  const errorMessages = mergeDeep(DEFAULT_MESSAGES, propMessages)

  const errorMessage = errorMessages[error?.type]

  return (
    <div className="ds-currency">
      <div
        className={classNames("ds-input", {
          "ds-input--invalid": isInvalid,
          "ds-input--disabled": disabled,
        })}
      >
        <label
          className="ds-input__label"
          htmlFor={name}
          id={`ds-label-${name}`}
          dangerouslySetInnerHTML={{ __html: label }}
          aria-label={label}
          aria-hidden
        />

        <div className="ds-input--currency">
          <div className="ds-input__field">
            <Controller
              name={name}
              rules={formRules}
              control={control}
              render={() => {
                return (
                  <>
                    <If
                      condition={isToRender}
                      renderIf={
                        <CurrencyInput
                          name={field.name}
                          id={id || field.name}
                          type={type}
                          inputMode={inputmode}
                          pattern={pattern}
                          prefix="R$"
                          defaultValue={getDefaultValue()}
                          decimalsLimit={decimalsLimit}
                          decimalScale={decimalScale}
                          decimalSeparator={decimalSeparator}
                          allowNegativeValue={false}
                          groupSeparator={groupSeparator}
                          ref={field.ref}
                          aria-required={required || ariaRequired}
                          aria-labelledby={`ds-label-${name}`}
                          aria-describedby={`${
                            isInvalid && errorMessage
                              ? `ds-error-message-${name} `
                              : ""
                          }ds-currency-description-${name}`}
                          disabled={disabled}
                          onBlur={(e) => {
                            const { value } = e.target
                            const parsedValue = value
                              ? parseFloat(
                                  value.replaceAll(".", "").replaceAll("R$", "")
                                )
                              : null
                            onBlur && onBlur(parsedValue)
                            field.onBlur()
                          }}
                          onValueChange={(value, name, { float }) => {
                            if (value !== undefined && value !== null) {
                              setHasValue(true)
                            } else {
                              setHasValue(false)
                            }
                            onChange && onChange(float)
                            field.onChange(float)
                          }}
                          {...props}
                        />
                      }
                    />
                  </>
                )
              }}
            />

            <span
              className={`ds-currency__type ${
                disabled ? "ds-currency__type--disabled" : ""
              }`}
              style={{ opacity: hasValue ? 1 : 0 }}
              aria-hidden="true"
              aria-label="valor em reais"
              id={`ds-currency_symbol-${name}`}
            >
              R$
            </span>
          </div>
          <div
            className="ds-input__icon ds-input__icon--right ds-currency__icon"
            aria-hidden="true"
          >
            <EditIcon
              width={24}
              height={24}
              className={`${disabled ? "ds-currency__icon--disabled" : ""}`}
            />
          </div>
        </div>

        <TextMessage
          message={errorMessage}
          id={`ds-error-message-${name}`}
          show={isInvalid && errorMessage}
        />

        <If
          condition={!isInvalid && description}
          renderIf={
            <p
              className="ds-input__message ds-input__message--description"
              id={`ds-currency-description-${name}`}
              aria-label={description}
              aria-hidden
            >
              {description}
            </p>
          }
        />
      </div>
    </div>
  )
}

export default Currency
